// Lib
import { useEffect, useRef } from 'react';
import { last } from 'lodash/fp';

// Services
import logger from '../../../../../../logger/logger';
import fetchEventSource from '../../../../../../utils/services/http/fetchEventSource';

// Utils
import { formatElementAsIdeaObject } from '../../../../../../../common/ai/aiResponseFormattingUtils';
import { convertIdeasToStructuredText } from '../../../../../../../common/ai/aiIdeaConversionUtils';
import { getLimitedIdeaContent } from '../../../../../../../common/ai/aiBoardConversionUtils';

// Types
import { AiResultType, UseAiToolState, isIdeaResponse } from './aiToolStateTypes';
import { PromptShortcut } from '../../../../../../../common/ai/aiPromptShortcutTypes';
import { MNElement } from '../../../../../../../common/elements/elementModelTypes';
import { AiIdea } from '../../../../../../../common/ai/aiConversationTypes';

// Constants
import { GENERATE_MORE_PROMPT_SHORTCUT, PROMPT_SHORTCUT_CONTEXT_LENGTH } from './aiToolStateConstants';
import { TIMES } from '../../../../../../../common/utils/timeUtil';

// Determines if we have prompt shortcuts to show
const getHasPromptShortcuts = (promptShortcutsList: PromptShortcut[]) =>
    promptShortcutsList.filter(({ id }) => id !== GENERATE_MORE_PROMPT_SHORTCUT.id).length > 0;

const createAssistantContext = (results: AiResultType[]) => {
    const content = [...results]
        .reverse()
        .filter(isIdeaResponse)
        .map((element) => formatElementAsIdeaObject(element as MNElement)) as AiIdea[];

    const limitedContent = getLimitedIdeaContent(content, PROMPT_SHORTCUT_CONTEXT_LENGTH).reverse();

    return convertIdeasToStructuredText(limitedContent);
};

const createPromptsFromBuffer = (responseBuffer: string, conversationId: string, isStreaming: boolean) =>
    responseBuffer
        .split('\n')
        .map((prompt) => prompt.trim())
        .filter((prompt) => prompt.startsWith('-'))
        .slice(0, 3)
        .map(
            (prompt: string, index: number): PromptShortcut => ({
                id: `followup-${conversationId}-${index}`,
                // remove punctuation from start and end of prompt
                promptText: prompt.replace(/^[-]|[!.?]+$/g, '').trim(),
                isStreaming,
                children: [],
            }),
        );

const useStreamPromptFollowups = (state: UseAiToolState): void => {
    const {
        chosenPromptShortcuts,
        currentConversation,
        currentConversationId,
        setPromptFollowupsStreaming,
        setAdditionalPromptShortcuts,
        setConversationError,
    } = state;

    const abortStreamingControllerRef = useRef<AbortController>();
    const abortStreamingTimerRef = useRef<NodeJS.Timeout>();

    const promptShortcut = last(chosenPromptShortcuts);
    const promptShortcutsList = promptShortcut?.children || [];
    const hasPromptShortcuts = getHasPromptShortcuts(promptShortcutsList);

    const streamingResults = currentConversation?.streamingResults || false;
    const streamingFollowups = currentConversation?.streamingFollowupPrompts || false;
    const fetched = currentConversation?.fetched || false;
    const hasError = currentConversation?.error || false;

    const endStreaming = (): void => {
        setPromptFollowupsStreaming(currentConversationId, false);
        abortStreamingControllerRef.current?.abort();
        clearTimeout(abortStreamingTimerRef.current);
    };

    const fetchFollowups = async () => {
        const assistantContext = createAssistantContext(currentConversation.results);
        if (!assistantContext.length) return;

        setPromptFollowupsStreaming(currentConversationId, true);

        const abortController = new AbortController();
        abortStreamingControllerRef.current = abortController;

        // if the stream is not closed within 10 seconds, abort it
        abortStreamingTimerRef.current = setTimeout(() => {
            endStreaming();
        }, TIMES.SECOND * 10);

        let responseBuffer = '';

        await fetchEventSource({
            url: 'ai/conversations/followups/stream',
            openWhenHidden: true,
            signal: AbortSignal.timeout(10000),
            data: {
                conversationId: currentConversationId,
                assistantContext,
            },
            onopen: async (response) => {
                if (response.ok) return; // don't do anything if the stream is open & ok
                // otherwise, proceed to error handling

                setConversationError(currentConversationId, 'Could not fetch followups');
                endStreaming();
            },
            onmessage: (event) => {
                try {
                    const { conversationId, delta } = JSON.parse(event.data);
                    if (!delta || typeof delta !== 'string') return;

                    responseBuffer += delta;
                    const prompts = createPromptsFromBuffer(responseBuffer, conversationId, true);

                    setAdditionalPromptShortcuts(conversationId, [GENERATE_MORE_PROMPT_SHORTCUT, ...prompts]);
                } catch (error) {
                    logger.error('AI: Could not parse stream result', error);
                }
            },
            onclose: () => {
                const prompts = createPromptsFromBuffer(responseBuffer, currentConversationId, false);
                setAdditionalPromptShortcuts(currentConversationId, [GENERATE_MORE_PROMPT_SHORTCUT, ...prompts]);
                endStreaming();
            },
            onerror: (event) => {
                setConversationError(currentConversationId, 'Could not fetch followup prompts.');
                endStreaming();
            },
        });
    };

    // fetch followups after result streaming has finished
    useEffect(() => {
        if (hasPromptShortcuts || streamingResults || streamingFollowups || hasError || !fetched) return;

        fetchFollowups();
    }, [hasPromptShortcuts, streamingResults, streamingFollowups, fetched]);

    // if we have started streaming results, end followup streaming
    useEffect(() => {
        if (streamingResults && streamingFollowups) {
            endStreaming();
        }
    }, [streamingResults, streamingFollowups]);
};

export default useStreamPromptFollowups;
